"use client";

import { useCallback, useEffect, useState } from "react";

import { countColumns } from "@/lib/grid-rows";

/**
 * The live column count of a CSS grid: attach `ref` to the grid element.
 * Re-read whenever the grid resizes (window, rail, rotation). 0 until the
 * first measure — `fullRows` / `pageSizeFor` leave counts untouched at 0, so
 * the server's first render and the first client paint agree.
 */
export function useGridColumns<T extends HTMLElement = HTMLElement>() {
  const [node, setNode] = useState<T | null>(null);
  const [columns, setColumns] = useState(0);
  const ref = useCallback((el: T | null) => setNode(el), []);

  useEffect(() => {
    if (!node) return;
    const read = () => setColumns(countColumns(getComputedStyle(node).gridTemplateColumns));
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref, columns };
}
